import React, { useState, useRef, useCallback } from "react";
import {
  UserCircle2,
  UploadCloud,
  X,
  Trash2,
  Check,
  AlertTriangle,
  Lock,
  Sparkles,
  LogIn,
  RefreshCw,
  Loader2,
  BadgeCheck,
} from "lucide-react";
import { COLORS } from "../constants";
import { pill, fontSerif } from "./styles";

const ACCEPT = ".glb,.gltf,.usdz,model/gltf-binary,model/gltf+json,model/vnd.usdz+zip";

export default function AvatarUploader({
  // avatar API
  avatar,
  status,
  uploadProgress,
  error,
  upload,
  clear,
  isRenderableInScene,
  // auth + unlock API
  user,
  unlocked,
  signedIn,
  onSignIn,
  onBuy,
  onVerify,
  verifying,
  verifyMessage,
  verifyError,
  compact = false,
}) {
  const [open, setOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef();

  const canUpload = signedIn && unlocked;

  const handleFile = useCallback(
    async (file) => {
      if (!file || !canUpload) return;
      await upload(file);
    },
    [upload, canUpload]
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (!canUpload) return;
    const f = e.dataTransfer.files && e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const triggerPicker = () => {
    if (!canUpload) return;
    inputRef.current && inputRef.current.click();
  };

  // Top-right pill label
  let pillLabel = "Avatar · $9.99";
  let PillIcon = Lock;
  if (avatar) {
    pillLabel = "Avatar";
    PillIcon = UserCircle2;
  } else if (canUpload) {
    pillLabel = "Your Avatar";
    PillIcon = UserCircle2;
  } else if (signedIn) {
    pillLabel = "Unlock · $9.99";
    PillIcon = Lock;
  }

  return (
    <>
      <button
        data-testid="zone-avatar-button"
        onClick={() => setOpen(true)}
        style={{
          ...pill,
          padding: compact ? "6px 12px" : "8px 14px",
          color: COLORS.ivory,
          fontFamily: fontSerif,
          fontSize: compact ? 11 : 12,
          letterSpacing: 2,
          textTransform: "uppercase",
          fontWeight: 600,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 8,
          border: `1px solid ${avatar || canUpload ? COLORS.gold : COLORS.navyMid}`,
        }}
      >
        <PillIcon size={13} color={COLORS.gold} />
        {pillLabel}
        {avatar && (
          <span
            data-testid="zone-avatar-dot"
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: COLORS.gold,
              boxShadow: `0 0 8px ${COLORS.gold}`,
            }}
          />
        )}
      </button>

      {open && (
        <div
          data-testid="avatar-modal"
          role="dialog"
          aria-modal="true"
          onClick={() => setOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9100,
            background: "rgba(5,10,28,0.72)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            display: "grid",
            placeItems: "center",
            fontFamily: fontSerif,
            color: COLORS.ivory,
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "relative",
              width: "min(94vw, 520px)",
              maxHeight: "92dvh",
              overflowY: "auto",
              background:
                "linear-gradient(180deg, rgba(11,21,48,0.94) 0%, rgba(15,31,74,0.94) 100%)",
              border: `1px solid ${COLORS.gold}`,
              borderRadius: 18,
              padding: "32px 32px 26px",
              boxShadow:
                "0 24px 80px rgba(0,0,0,0.65), 0 0 70px rgba(230,180,80,0.18)",
            }}
          >
            <button
              data-testid="avatar-modal-close"
              onClick={() => setOpen(false)}
              aria-label="Close"
              style={{
                position: "absolute",
                top: 14,
                right: 14,
                background: "transparent",
                border: "none",
                color: COLORS.ivory,
                opacity: 0.7,
                cursor: "pointer",
                padding: 6,
              }}
            >
              <X size={16} />
            </button>

            <div
              style={{
                fontSize: 10,
                letterSpacing: 5,
                color: COLORS.gold,
                textTransform: "uppercase",
                marginBottom: 8,
              }}
            >
              Your Avatar
            </div>
            <h2
              style={{
                margin: 0,
                fontSize: 24,
                color: COLORS.goldBright,
                letterSpacing: 0.5,
              }}
            >
              {!signedIn
                ? "Sign in to Continue"
                : canUpload
                ? "Upload a 3D model"
                : "Custom Avatar Pass"}
            </h2>

            {/* ---------- State A: signed out ---------- */}
            {!signedIn && (
              <div data-testid="avatar-signin-card" style={{ marginTop: 18 }}>
                <p
                  style={{
                    fontSize: 14,
                    lineHeight: 1.6,
                    opacity: 0.9,
                    fontStyle: "italic",
                    marginTop: 0,
                  }}
                >
                  Sign in with Google to purchase or restore your Custom Avatar
                  Pass, then upload <strong>.glb</strong>,{" "}
                  <strong>.gltf</strong>, or <strong>.usdz</strong> models for
                  Walk mode and future immersive sessions.
                </p>
                <div
                  style={{
                    border: `1px solid ${COLORS.gold}`,
                    borderRadius: 14,
                    padding: 22,
                    background: "rgba(11,21,48,0.55)",
                    textAlign: "center",
                  }}
                >
                  <Sparkles
                    size={26}
                    color={COLORS.gold}
                    style={{ marginBottom: 6 }}
                    aria-hidden="true"
                  />
                  <button
                    data-testid="avatar-signin-button"
                    onClick={onSignIn}
                    style={{
                      padding: "12px 26px",
                      borderRadius: 999,
                      border: "none",
                      cursor: "pointer",
                      background: COLORS.gold,
                      color: COLORS.navyDeep,
                      fontFamily: fontSerif,
                      fontSize: 12,
                      letterSpacing: 2.5,
                      textTransform: "uppercase",
                      fontWeight: 700,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      boxShadow: "0 8px 24px rgba(230,180,80,0.25)",
                    }}
                  >
                    <LogIn size={14} />
                    Continue with Google
                  </button>
                </div>
              </div>
            )}

            {/* ---------- State B: signed in, not paid ---------- */}
            {signedIn && !unlocked && (
              <>
                <p
                  style={{
                    marginTop: 10,
                    marginBottom: 22,
                    fontSize: 14,
                    lineHeight: 1.6,
                    opacity: 0.9,
                    fontStyle: "italic",
                  }}
                >
                  Signed in as{" "}
                  <strong>{user && (user.display_name || user.email)}</strong>.
                  Unlock the right to bring your own 3D avatar into the Quad — a
                  one-time pass tied to your account.
                </p>
                <div
                  data-testid="avatar-locked-card"
                  style={{
                    border: `1px solid ${COLORS.gold}`,
                    borderRadius: 14,
                    padding: 22,
                    background: "rgba(11,21,48,0.55)",
                    textAlign: "center",
                  }}
                >
                  <Sparkles
                    size={28}
                    color={COLORS.gold}
                    style={{ marginBottom: 6 }}
                    aria-hidden="true"
                  />
                  <div
                    style={{
                      fontSize: 11,
                      letterSpacing: 3,
                      color: COLORS.gold,
                      textTransform: "uppercase",
                      marginBottom: 4,
                    }}
                  >
                    One-time · Tied to your Google account
                  </div>
                  <div
                    style={{
                      fontSize: 28,
                      color: COLORS.goldBright,
                      letterSpacing: 0.5,
                      marginBottom: 14,
                    }}
                  >
                    $9.99
                  </div>
                  <button
                    data-testid="avatar-buy-button"
                    onClick={onBuy}
                    style={{
                      padding: "12px 26px",
                      borderRadius: 999,
                      border: "none",
                      cursor: "pointer",
                      background: COLORS.gold,
                      color: COLORS.navyDeep,
                      fontFamily: fontSerif,
                      fontSize: 12,
                      letterSpacing: 2.5,
                      textTransform: "uppercase",
                      fontWeight: 700,
                      boxShadow: "0 8px 24px rgba(230,180,80,0.25)",
                    }}
                  >
                    Get Custom Avatar · $9.99
                  </button>

                  <div style={{ marginTop: 18 }}>
                    <button
                      data-testid="avatar-verify-button"
                      onClick={() => onVerify && onVerify()}
                      disabled={verifying}
                      style={{
                        background: "transparent",
                        border: `1px solid ${COLORS.navyMid}`,
                        borderRadius: 999,
                        padding: "8px 16px",
                        color: COLORS.ivory,
                        cursor: verifying ? "default" : "pointer",
                        fontFamily: fontSerif,
                        fontSize: 11,
                        letterSpacing: 2,
                        textTransform: "uppercase",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        opacity: verifying ? 0.7 : 1,
                      }}
                    >
                      {verifying ? (
                        <Loader2 size={12} className="spin" />
                      ) : (
                        <RefreshCw size={12} />
                      )}
                      {verifying ? "Checking Stripe…" : "Already paid? Verify now"}
                    </button>
                  </div>

                  {verifyMessage && (
                    <div
                      data-testid="avatar-verify-message"
                      style={{
                        marginTop: 12,
                        fontSize: 11,
                        letterSpacing: 1,
                        color: COLORS.gold,
                      }}
                    >
                      {verifyMessage}
                    </div>
                  )}
                  {verifyError && (
                    <div
                      data-testid="avatar-verify-error"
                      style={{
                        marginTop: 12,
                        fontSize: 11,
                        color: "#ffd5c8",
                        lineHeight: 1.5,
                      }}
                    >
                      {verifyError}
                    </div>
                  )}

                  <div
                    style={{
                      marginTop: 14,
                      fontSize: 10,
                      letterSpacing: 2,
                      color: "rgba(243,234,212,0.55)",
                      textTransform: "uppercase",
                    }}
                  >
                    Secure checkout via Stripe · returns to the Quad on success
                  </div>
                </div>
              </>
            )}

            {/* ---------- State C: signed in + paid ---------- */}
            {canUpload && (
              <>
                <p
                  style={{
                    marginTop: 10,
                    marginBottom: 22,
                    fontSize: 14,
                    lineHeight: 1.55,
                    opacity: 0.85,
                    fontStyle: "italic",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <BadgeCheck size={16} color={COLORS.gold} />
                  Pass active for{" "}
                  <strong>{user && (user.display_name || user.email)}</strong>.
                </p>

                {avatar && (
                  <div
                    data-testid="avatar-current"
                    style={{
                      ...pill,
                      background: "rgba(11,21,48,0.55)",
                      padding: "10px 14px",
                      marginBottom: 18,
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                    }}
                  >
                    <Check size={14} color={COLORS.gold} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 10,
                          letterSpacing: 3,
                          color: COLORS.gold,
                          textTransform: "uppercase",
                        }}
                      >
                        Current · {avatar.ext}
                      </div>
                      <div
                        data-testid="avatar-current-filename"
                        style={{
                          fontSize: 13,
                          color: COLORS.ivory,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {avatar.original_filename}
                      </div>
                    </div>
                    <button
                      data-testid="avatar-clear"
                      onClick={clear}
                      style={{
                        background: "transparent",
                        border: `1px solid ${COLORS.navyMid}`,
                        color: COLORS.ivory,
                        cursor: "pointer",
                        borderRadius: 999,
                        padding: "6px 10px",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        fontFamily: fontSerif,
                        fontSize: 11,
                        letterSpacing: 1.5,
                        textTransform: "uppercase",
                      }}
                    >
                      <Trash2 size={12} />
                      Clear
                    </button>
                  </div>
                )}

                <div
                  data-testid="avatar-dropzone"
                  onClick={triggerPicker}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                  }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={onDrop}
                  style={{
                    border: `2px dashed ${dragOver ? COLORS.gold : COLORS.navyMid}`,
                    borderRadius: 14,
                    background: dragOver
                      ? "rgba(230,180,80,0.06)"
                      : "rgba(11,21,48,0.35)",
                    padding: "30px 20px",
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "border-color .2s, background .2s",
                  }}
                >
                  <UploadCloud
                    size={36}
                    color={COLORS.gold}
                    style={{ marginBottom: 10 }}
                    aria-hidden="true"
                  />
                  <div
                    style={{
                      fontSize: 15,
                      color: COLORS.ivory,
                      marginBottom: 4,
                    }}
                  >
                    Drag &amp; drop your model here
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      letterSpacing: 2,
                      color: COLORS.gold,
                      textTransform: "uppercase",
                      marginBottom: 14,
                    }}
                  >
                    or click to browse · .glb · .gltf · .usdz · ≤ 30 MB
                  </div>
                  <input
                    ref={inputRef}
                    data-testid="avatar-file-input"
                    type="file"
                    accept={ACCEPT}
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const f = e.target.files && e.target.files[0];
                      if (f) handleFile(f);
                      e.target.value = "";
                    }}
                  />
                </div>

                {status === "uploading" && (
                  <div data-testid="avatar-uploading" style={{ marginTop: 18 }}>
                    <div
                      style={{
                        fontSize: 10,
                        letterSpacing: 3,
                        color: COLORS.gold,
                        textTransform: "uppercase",
                        marginBottom: 6,
                      }}
                    >
                      Uploading · {uploadProgress}%
                    </div>
                    <div
                      style={{
                        height: 6,
                        background: "rgba(11,21,48,0.7)",
                        border: `1px solid ${COLORS.navyMid}`,
                        borderRadius: 999,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          height: "100%",
                          width: `${uploadProgress}%`,
                          background: COLORS.gold,
                          transition: "width .2s",
                        }}
                      />
                    </div>
                  </div>
                )}

                {status === "error" && error && (
                  <div
                    data-testid="avatar-error"
                    style={{
                      marginTop: 18,
                      ...pill,
                      border: "1px solid #c08070",
                      padding: "10px 14px",
                      color: "#ffd5c8",
                      fontSize: 12,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <AlertTriangle size={14} />
                    {error}
                  </div>
                )}

                {avatar && !isRenderableInScene && (
                  <div
                    data-testid="avatar-usdz-notice"
                    style={{
                      marginTop: 18,
                      ...pill,
                      padding: "10px 14px",
                      color: COLORS.ivory,
                      fontSize: 12,
                      fontStyle: "italic",
                      opacity: 0.85,
                    }}
                  >
                    Your .usdz is safely stored — in-scene rendering will arrive
                    in a future update. Meanwhile, your default avatar will be
                    used in Walk mode.
                  </div>
                )}

                <div
                  style={{
                    marginTop: 22,
                    fontSize: 10,
                    letterSpacing: 3,
                    color: "rgba(243,234,212,0.55)",
                    textTransform: "uppercase",
                    textAlign: "center",
                  }}
                >
                  Switch to Walk mode to see your avatar in the Quad
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
